import React, { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Award } from "lucide-react";
import { toast } from "sonner";
import api from "config/axiosConfig";
import { awardQueryKeys, useAwardGivingBudgetQuery } from "hooks/queries/useAwardBalanceQuery";
import ConfirmAwardDialog from "components/Common/ConfirmAwardDialog";
import EmptyState from "ui/EmptyState";
import { Button } from "ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "ui/Dialog";
import { useUser } from "util/UserContext";
import { apiRoutes } from "util/apiRoutes";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";

export interface ContentAwardSummary {
  totalAwards: number;
  viewerHasAwarded: boolean;
  viewerCanAward: boolean;
}

interface ContentAwardButtonProps {
  entityType: string;
  entityId: number;
  authorUserId?: number | string | null;
  targetLabel?: string | null;
  className?: string;
  compact?: boolean;
  /**
   * When true, lays out the icon and count vertically (icon on top, count
   * stacked below) for the media-viewer right-rail action overlay.
   */
  vertical?: boolean;
}

function parsePositiveUserId(value: number | string | null | undefined): number | null {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Shows aggregate awards and lets the signed-in viewer spend one award on this entity.
 */
const ContentAwardButton: React.FC<ContentAwardButtonProps> = ({
  entityType,
  entityId,
  authorUserId,
  targetLabel,
  className = "",
  compact = false,
  vertical = false,
}) => {
  const { currentUser, setShowAuthModal } = useUser();
  const queryClient = useQueryClient();
  const ui = getStoryUiStrings(resolveStoryLocale());
  const [showZeroBalanceDialog, setShowZeroBalanceDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const awardBudgetQuery = useAwardGivingBudgetQuery(Boolean(currentUser));

  const summaryQuery = useQuery({
    queryKey: ["awardSummary", entityType, entityId],
    queryFn: async (): Promise<ContentAwardSummary> => {
      const res = await api.get<ContentAwardSummary>(apiRoutes.awards.entity(entityType, entityId));
      return {
        totalAwards: res.data?.totalAwards ?? 0,
        viewerHasAwarded: res.data?.viewerHasAwarded ?? false,
        viewerCanAward: res.data?.viewerCanAward ?? true,
      };
    },
    staleTime: 30_000,
  });

  const giveMutation = useMutation({
    mutationFn: async () => {
      const idempotencyKey = currentUser
        ? `content-award:${entityType}:${entityId}:${currentUser.id}`
        : undefined;
      await api.post(apiRoutes.awards.giveContent(entityType, entityId), {
        idempotency_key: idempotencyKey,
      });
    },
    onSuccess: async () => {
      toast.success(ui.awardGiven);
      await queryClient.invalidateQueries({ queryKey: ["awardSummary", entityType, entityId] });
      await queryClient.invalidateQueries({ queryKey: awardQueryKeys.balance() });
    },
    onError: (err: unknown) => {
      const status =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "status" in err.response
          ? (err.response as { status?: number }).status
          : undefined;
      const responseMessage =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data
          ? String((err.response.data as { message?: string }).message || "")
          : "";
      if (status === 409) {
        void queryClient.invalidateQueries({ queryKey: awardQueryKeys.balance() });
        setShowZeroBalanceDialog(true);
        return;
      }
      if (status === 400 && responseMessage === "AWARD_SELF_NOT_ALLOWED") {
        return;
      }
      toast.error(ui.awardError);
    },
  });

  const handleViewAllowanceDetails = useCallback(() => {
    setShowZeroBalanceDialog(false);
    window.location.assign("/settings/account");
  }, []);

  const submitAward = useCallback(() => {
    setShowConfirmDialog(false);
    giveMutation.mutate();
  }, [giveMutation]);

  const onAward = useCallback(() => {
    if (!currentUser) {
      setShowAuthModal(true);
      toast.message(ui.awardLogin);
      return;
    }
    if (summaryQuery.data?.viewerHasAwarded) {
      return;
    }
    if (summaryQuery.data?.viewerCanAward === false) {
      return;
    }
    if ((awardBudgetQuery.data?.balance ?? null) !== null && (awardBudgetQuery.data?.balance ?? 0) <= 0) {
      setShowZeroBalanceDialog(true);
      return;
    }
    setShowConfirmDialog(true);
  }, [
    awardBudgetQuery.data?.balance,
    currentUser,
    setShowAuthModal,
    summaryQuery.data?.viewerCanAward,
    summaryQuery.data?.viewerHasAwarded,
    ui.awardLogin,
  ]);

  const total = summaryQuery.data?.totalAwards ?? 0;
  const hasAwarded = summaryQuery.data?.viewerHasAwarded ?? false;
  const normalizedAuthorUserId = parsePositiveUserId(authorUserId);
  const isOwnContent =
    typeof currentUser?.id === "number"
    && normalizedAuthorUserId !== null
    && currentUser.id === normalizedAuthorUserId;
  const canAward = (summaryQuery.data?.viewerCanAward ?? true) && !isOwnContent;
  const disabled = giveMutation.isPending || summaryQuery.isPending || hasAwarded || !canAward;
  const buttonClassName = compact
    ? `inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold text-amber-700 disabled:cursor-default disabled:opacity-50 dark:text-amber-300 ${
        disabled ? "" : "hover:bg-amber-100 dark:hover:bg-amber-900/40"
      }`
    : `inline-flex h-7 items-center gap-1 rounded-full px-2 text-sm font-semibold text-amber-700 disabled:cursor-default disabled:opacity-50 dark:text-amber-300 ${
        disabled ? "" : "hover:bg-amber-100 dark:hover:bg-amber-900/40"
      }`;

  const verticalButtonClassName = `flex flex-col items-center gap-0.5 rounded-full p-1 text-amber-300 disabled:cursor-default disabled:opacity-50 ${
    disabled ? "" : "hover:bg-white/15"
  } transition-colors`;

  const countLabel = ui.awardCount.replace("{count}", String(total));

  if (vertical) {
    return (
      <div
        className={`flex flex-col items-center ${className}`.trim()}
        title={countLabel}
      >
        <button
          type="button"
          onClick={onAward}
          disabled={disabled}
          className={verticalButtonClassName}
          aria-label={ui.awardButton}
        >
          <Award className="size-7" aria-hidden />
        </button>
        <span className="text-xs font-semibold tabular-nums text-eoe-gray dark:text-eoe-dark-icon-stroke">
          {total}
        </span>
        <span className="sr-only">{countLabel}</span>
        <ConfirmAwardDialog
          open={showConfirmDialog}
          targetLabel={targetLabel || entityType}
          isSubmitting={giveMutation.isPending}
          onConfirm={submitAward}
          onOpenChange={setShowConfirmDialog}
        />
        <Dialog open={showZeroBalanceDialog} onOpenChange={setShowZeroBalanceDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="sr-only">
              <DialogTitle>{ui.awardZeroBalanceTitle}</DialogTitle>
              <DialogDescription>{ui.awardZeroBalanceSubtitle}</DialogDescription>
            </DialogHeader>
            <EmptyState
              icon={<Award aria-hidden />}
              title={ui.awardZeroBalanceTitle}
              subtitle={ui.awardZeroBalanceSubtitle}
              cta={
                <Button
                  type="button"
                  onClick={handleViewAllowanceDetails}
                >
                  {ui.awardViewAllowanceDetails}
                </Button>
              }
              className="px-2 pt-6"
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-1 ${className}`.trim()}
      title={countLabel}
    >
      <button
        type="button"
        onClick={onAward}
        disabled={disabled}
        className={buttonClassName}
        aria-label={ui.awardButton}
      >
        <Award className={compact ? "size-4" : "size-5"} aria-hidden />
        <span className="tabular-nums">{total}</span>
        {!compact ? <span className="sr-only">{countLabel}</span> : null}
      </button>
      <ConfirmAwardDialog
        open={showConfirmDialog}
        targetLabel={targetLabel || entityType}
        isSubmitting={giveMutation.isPending}
        onConfirm={submitAward}
        onOpenChange={setShowConfirmDialog}
      />
      <Dialog open={showZeroBalanceDialog} onOpenChange={setShowZeroBalanceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="sr-only">
            <DialogTitle>{ui.awardZeroBalanceTitle}</DialogTitle>
            <DialogDescription>{ui.awardZeroBalanceSubtitle}</DialogDescription>
          </DialogHeader>
          <EmptyState
            icon={<Award aria-hidden />}
            title={ui.awardZeroBalanceTitle}
            subtitle={ui.awardZeroBalanceSubtitle}
            cta={
              <Button
                type="button"
                onClick={handleViewAllowanceDetails}
              >
                {ui.awardViewAllowanceDetails}
              </Button>
            }
            className="px-2 pt-6"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentAwardButton;
